import React, { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    Container,
    Card,
    CardContent,
    Paper,
    useTheme,
    alpha,
    Stack,
    Chip
} from "@mui/material";
import { designTokens } from '../theme/designTokens';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Link } from 'react-router-dom';
import { howtos } from '../metadata/howtos';

const HowTos = () => {
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: designTokens.spacing['3xl'] / 8 }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        p: designTokens.spacing['6xl'] / 8,
                        mb: designTokens.spacing['6xl'] / 8,
                        textAlign: 'center',
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
                    }}
                >
                    <Stack spacing={designTokens.spacing['2xl'] / 8} alignItems="center">
                        <Box
                            sx={{
                                width: designTokens.spacing['7xl'],
                                height: designTokens.spacing['7xl'],
                                borderRadius: designTokens.borderRadius.full,
                                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: designTokens.shadows.xl,
                            }}
                        >
                            <MenuBookIcon sx={{ fontSize: designTokens.spacing['5xl'], color: 'white' }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{
                                    fontWeight: designTokens.fontWeight.bold,
                                    color: 'text.primary',
                                    mb: designTokens.spacing.lg / 8,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                How-To Guides
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: designTokens.fontWeight.normal,
                                    maxWidth: 600,
                                    mx: 'auto',
                                    lineHeight: designTokens.lineHeight.relaxed,
                                }}
                            >
                                Step-by-step guides to help you master RWE DataLab and unlock the full potential of your research
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Quick Stats */}
                <Grid container spacing={designTokens.spacing['2xl'] / 8} sx={{ mb: designTokens.spacing['6xl'] / 8 }}>
                    <Grid size={{ xs: 12, md: 4 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: designTokens.spacing['2xl'] / 8,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                height: '100%',
                            }}
                        >
                            <Typography
                                variant="h3"
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: designTokens.fontWeight.bold,
                                    mb: designTokens.spacing.sm / 8,
                                }}
                            >
                                {howtos.length}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                Step-by-Step Guides
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                height: '100%',
                            }}
                        >
                            <Typography
                                variant="h3"
                                sx={{
                                    color: 'secondary.main',
                                    fontWeight: 700,
                                    mb: 1,
                                }}
                            >
                                100+
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                Detailed Instructions
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                height: '100%',
                            }}
                        >
                            <Typography
                                variant="h3"
                                sx={{
                                    color: 'success.main',
                                    fontWeight: 700,
                                    mb: 1,
                                }}
                            >
                                24/7
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                Available Support
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Guides Section */}
                <Box sx={{ mb: 6 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                        <PlayCircleOutlineIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                            }}
                        >
                            Interactive Tutorials
                        </Typography>
                    </Stack>

                    <Stack spacing={2}>
                        {howtos.map((guide, index) => (
                            <Accordion
                                key={index}
                                expanded={expanded === `panel${index + 1}`}
                                onChange={handleChange(`panel${index + 1}`)}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    boxShadow: expanded === `panel${index + 1}`
                                        ? `0 8px 24px ${alpha(theme.palette.secondary.main, 0.12)}`
                                        : `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.main, 0.08)}`,
                                    },
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                color: expanded === `panel${index + 1}`
                                                    ? 'secondary.main'
                                                    : 'text.secondary',
                                                transition: 'color 0.2s ease-in-out',
                                            }}
                                        />
                                    }
                                    aria-controls={`panel${index + 1}bh-content`}
                                    id={`panel${index + 1}bh-header`}
                                    sx={{
                                        backgroundColor: expanded === `panel${index + 1}`
                                            ? alpha(theme.palette.secondary.main, 0.04)
                                            : 'transparent',
                                        borderRadius: 2,
                                        minHeight: 64,
                                        px: 3,
                                        '&.Mui-expanded': {
                                            borderBottomLeftRadius: 0,
                                            borderBottomRightRadius: 0,
                                        },
                                        transition: 'background-color 0.2s ease-in-out',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                        <Chip
                                            label={`Guide ${index + 1}`}
                                            size="small"
                                            sx={{
                                                bgcolor: expanded === `panel${index + 1}`
                                                    ? 'secondary.main'
                                                    : alpha(theme.palette.secondary.main, 0.1),
                                                color: expanded === `panel${index + 1}`
                                                    ? 'white'
                                                    : 'secondary.main',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                minWidth: 70,
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 500,
                                                color: expanded === `panel${index + 1}`
                                                    ? 'secondary.main'
                                                    : 'text.primary',
                                                transition: 'color 0.2s ease-in-out',
                                                flex: 1,
                                            }}
                                        >
                                            {guide.title}
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: 3,
                                        py: 3,
                                        backgroundColor: 'background.paper',
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            '& ol': {
                                                paddingLeft: 2,
                                                margin: 0,
                                            },
                                            '& ol > li': {
                                                marginBottom: 2,
                                                color: 'text.primary',
                                                fontSize: '1rem',
                                                lineHeight: 1.6,
                                                paddingLeft: 1,
                                            },
                                            '& ul': {
                                                paddingLeft: 2,
                                                marginTop: 1,
                                            },
                                            '& ul > li': {
                                                marginBottom: 0.5,
                                                color: 'text.secondary',
                                                fontSize: '0.9rem',
                                                lineHeight: 1.5,
                                            },
                                        }}
                                    >
                                        <ol>
                                            {guide.steps.map((step, stepIndex) => (
                                                <li key={stepIndex}>
                                                    {typeof step === 'object' ? (
                                                        step.subList ? (
                                                            <ul>
                                                                {step.subList.map((subStep, subIndex) => (
                                                                    <li key={subIndex}>{subStep}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <>
                                                                {step.text}{' '}
                                                                <Link
                                                                    to={step.link.href}
                                                                    style={{ color: theme.palette.secondary.main, fontWeight: 500 }}
                                                                >
                                                                    {step.link.label}
                                                                </Link>
                                                            </>
                                                        )
                                                    ) : (
                                                        step
                                                    )}
                                                </li>
                                            ))}
                                        </ol>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>

                {/* Contact Support Section */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                            px: 4,
                            py: 3,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <SupportAgentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                            <Box>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        mb: 0.5,
                                    }}
                                >
                                    Need Personal Assistance?
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    Our expert support team is ready to guide you through any process step-by-step.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                            <Grid size={{ xs: 12, md: 6 }} >
                                <Card
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 3,
                                        height: '100%',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 16px auto',
                                            }}
                                        >
                                            <PhoneIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Live Support
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'primary.main',
                                                fontWeight: 500,
                                                mb: 1,
                                            }}
                                        >
                                            (317) 207-2136
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            Get instant help with guided tutorials
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }} >
                                <Card
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 3,
                                        height: '100%',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.main, 0.08)}`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 16px auto',
                                            }}
                                        >
                                            <EmailIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Email Support
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'secondary.main',
                                                fontWeight: 500,
                                                mb: 1,
                                            }}
                                        >
                                            support@rwedatalab.com
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            Request detailed walkthrough guides
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

export default HowTos;